import api from '@/api';
import type { AuthResponse } from '@/types/api/auth';
import type {
    ProfileData,
    ProfileEditResponse,
    ProfilePictureUpdateRequest,
} from '@/types/profile';
import type { DeletionReason } from '@/types/profile';
import { ApiFactory } from './base/ApiFactory';

/**
 * Service class for interacting with the Profile API endpoints.
 * Handles profile data retrieval and profile picture updates.
 */
class ProfileService extends ApiFactory<ProfileData> {
    /**
     * Creates an instance of ProfileService.
     */
    constructor() {
        super('/profile', api);
    }

    /**
     * Gets the user's profile data for editing.
     * @returns Promise with the profile edit response
     */
    async getProfileEdit(): Promise<ProfileEditResponse> {
        return this.get<ProfileEditResponse>(`${this.resource}/edit`);
    }

    /**
     * Updates the user's profile picture.
     * @param profilePicture - The new profile picture (File for upload or URL string)
     * @returns Promise with the profile picture update response
     */
    async updateProfilePicture(image: File | string): Promise<ProfilePictureUpdateRequest> {
        // If it's a File object, create FormData for multipart upload
        if (image instanceof File) {
            const formData = new FormData();
            formData.append('image', image);

            // Use direct API call for FormData to handle multipart properly
            const response = await this.api.patch<ProfilePictureUpdateRequest>(
                `${this.resource}/picture-update`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            );
            return response.data;
        }

        // If it's a URL string, send as JSON using the patch method
        return this.patch<ProfilePictureUpdateRequest>(`${this.resource}/picture-update`, {
            image,
        });
    }

    /**
     * Updates the user's password from profile settings.
     * @param email - User's email address
     * @param password - New password
     * @param password_confirmation - Password confirmation
     * @returns Promise with auth response
     */
    async updatePassword(
        email: string,
        password: string,
        password_confirmation: string,
    ): Promise<AuthResponse> {
        return this.post<AuthResponse>(`${this.resource}/new-password/update`, {
            email,
            password,
            password_confirmation,
        });
    }

    /**
     * Sends OTP for profile password update.
     * @param email - User's email address
     * @returns Promise that resolves when OTP is sent
     */
async sendProfilePasswordOtp(email: string, channel?: string): Promise<void> {
        await this.post<void>(`${this.resource}/new-password/otp/send`, { email, ...(channel && { channel }) });
    }

    async verifyProfilePasswordOtp(email: string, otp: string): Promise<any> {
        const response = await this.post<any>(`${this.resource}/new-password/otp/verify`, {
            email: email,
            otp: otp,
        });
        return response;
    }

    async resendProfilePasswordOtp(email: string, channel?: string): Promise<void> {
        await this.post<void>(`${this.resource}/new-password/otp/resend`, { email, ...(channel && { channel }) });
    }

    /**
     * Updates the user's profile information.
     * @param profileData - The profile data to update
     * @returns Promise with the profile update response
     */
    async updateProfile(profileData: Partial<ProfileData>): Promise<ProfileEditResponse> {
        return this.post<ProfileEditResponse>(`${this.resource}/update`, profileData);
    }

    /**
     * Sends OTP for profile information update.
     * @param email - User's email address
     * @returns Promise that resolves when OTP is sent
     */
async sendProfileUpdateOtp(email: string, channel?: string): Promise<void> {
        await this.post<void>(`${this.resource}/otp/send`, { email, ...(channel && { channel }) });
    }

    async verifyProfileUpdateOtp(email: string, otp: string): Promise<any> {
        const response = await this.post<any>(`${this.resource}/otp/verify`, {
            email: email,
            otp: otp,
        });
        return response;
    }

    async resendProfileUpdateOtp(email: string, channel?: string): Promise<void> {
        await this.post<void>(`${this.resource}/otp/resend`, { email, ...(channel && { channel }) });
    }

    /**
     * Schedules the user's account for deletion.
     * @param reason - The reason for deletion
     * @param otherReason - Additional details if reason is 'OTHERS'
     */
    async scheduleDeletion(reason: DeletionReason, otherReason?: string): Promise<void> {
        await this.post<void>(`${this.resource}/deletion`, {
            reason,
            ...(otherReason ? { other_reason: otherReason } : {}),
        });
    }
}

export const profileService = new ProfileService();
export default ProfileService;
